import { BaseFieldProps, Field, InjectedFormProps, reduxForm } from 'redux-form';

import { Address } from 'dashboard/components/AddressInput';
import AddressInputField from 'dashboard/components/FormControls/AddressInputField';
import DateInputField from 'dashboard/components/FormControls/DateInputField';
import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import InputField from 'dashboard/components/FormControls/InputField';
import PhoneInputField from 'dashboard/components/FormControls/PhoneInputField';
import ThemedMaskedInput, { ThemedMaskedInputProps } from 'dashboard/components/ThemedMaskedInput';
import Form from 'dashboard/layouts/FormLayout/Form';
import FormSection from 'dashboard/layouts/FormLayout/Section';
import Button from 'design-system/components/Button';
import { HStack } from 'design-system/components/Stack';
import PiiAttributes from 'lib/interfaces/PiiAttributes';
import {
  isLength,
  isMaxLength,
  isValidDate,
  sanitize,
  withMessage,
} from 'lib/utils/formValidation';

const validate = {
  'reference-id': [
    withMessage('Please limit your Reference ID to 255 characters', isMaxLength(255)),
  ],
  [PiiAttributes.Birthdate]: [withMessage('Enter a valid date', isValidDate)],
  [PiiAttributes.SocialSecurityNumber]: [
    withMessage('Enter a valid social security number', sanitize(/\D/g, isLength(9))),
  ],
};

interface FormCreateReportProfileProps {
  hideReferenceId: boolean;
  handleCancel: () => void;
  readOnly?: boolean;
}

export const FormCreateReportProfile = ({
  submitting,
  hideReferenceId,
  handleSubmit,
  handleCancel,
  readOnly,
}: FormCreateReportProfileProps &
  InjectedFormProps<FormCreateReportProfileData, FormCreateReportProfileProps>) => {
  return (
    <Form onSubmit={handleSubmit}>
      {!hideReferenceId && (
        <Field
          id='create-report-profile__reference-id'
          label='Reference ID (optional)'
          name='reference-id'
          disabled={submitting || readOnly}
          component={InputField}
          validate={validate['reference-id']}
          data-test='create-report-profile__reference-id'
        />
      )}
      <FormControlLabel>Name (optional)</FormControlLabel>
      <FormSection cols={2}>
        <Field
          id='create-report-profile__name-first'
          name={PiiAttributes.NameFirst}
          component={InputField}
          disabled={submitting || readOnly}
          data-test='create-report-profile__name-first'
          placeholder='First name'
        />
        <Field
          id='create-report-profile__name-last'
          name={PiiAttributes.NameLast}
          component={InputField}
          disabled={submitting || readOnly}
          data-test='create-report-profile__name-last'
          placeholder='Last name'
        />
      </FormSection>
      <Field
        id='create-report-profile__birthdate'
        label='Birthdate (optional)'
        name={PiiAttributes.Birthdate}
        disabled={submitting || readOnly}
        component={DateInputField}
        validate={validate[PiiAttributes.Birthdate]}
        data-test='create-report-profile__birthdate'
      />
      <Field
        id='create-report-profile__address'
        label='Address (optional)'
        name={PiiAttributes.Address}
        type='text'
        disabled={submitting || readOnly}
        component={AddressInputField}
        placeholder='Address'
        data-test='create-report-profile__address'
      />
      <Field<ThemedMaskedInputProps & BaseFieldProps>
        id='create-report-profile__social-security-number'
        label='Social Security Number (optional)'
        name={PiiAttributes.SocialSecurityNumber}
        validate={validate[PiiAttributes.SocialSecurityNumber]}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        component={ThemedMaskedInput}
        disabled={submitting || readOnly}
        mask={[/\d/, /\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
        type='text'
        placeholder='###-##-####'
        data-test='create-report-profile__social-security-number'
      />
      <Field
        label='Phone Number (optional)'
        name={PiiAttributes.PhoneNumber}
        disabled={submitting || readOnly}
        component={PhoneInputField}
        data-test='create-report-profile__phone-number'
      />
      <HStack equal={true}>
        <Button
          variant='basic'
          id='create-report-profile__cancel'
          data-test='create-report-profile__cancel'
          disabled={submitting}
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          type='submit'
          data-test='create-report-profile__submit'
          disabled={submitting}
          isPending={submitting}
        >
          Create Report
        </Button>
      </HStack>
    </Form>
  );
};

export interface FormCreateReportProfileData {
  'reference-id'?: string;
  [PiiAttributes.NameFirst]?: string;
  [PiiAttributes.NameLast]?: string;
  [PiiAttributes.Birthdate]?: string;
  [PiiAttributes.Address]?: Address;
  [PiiAttributes.CountryCode]?: string;
  [PiiAttributes.SocialSecurityNumber]?: string;
  [PiiAttributes.PhoneNumber]?: string;
}

export const FORM_CREATE_REPORT_PROFILE_NAME = 'formCreateReportProfileData';

export default reduxForm<FormCreateReportProfileData, FormCreateReportProfileProps>({
  form: FORM_CREATE_REPORT_PROFILE_NAME,
})(FormCreateReportProfile);
